import type { Case, CaseStatus, CaseType } from '@core/domain/model';
import { failure, guard, success, type DataResult } from '@core/domain/result';
import { formatLinks, newId, toCase } from './mappers';
import type { Database } from './sqlite';

/** Фильтр списка дел. Пустые поля означают «не фильтровать». */
export interface CaseFilter {
  statuses?: CaseStatus[];
  types?: CaseType[];
  clientId?: string | null;
  tagIds?: string[];
}

const ALL_STATUSES: CaseStatus[] = ['Active', 'Suspended', 'Completed'];
const ALL_TYPES: CaseType[] = ['Consultation', 'ClaimWork', 'CourtCase', 'Enforcement', 'Other'];

/** Шаг между соседями в своём порядке — тот же, что на Android. */
const SORT_STEP = 1024;

export function caseRepository(db: Database, now: () => number = Date.now) {
  const findRaw = async (id: string) =>
    (await db.query('SELECT * FROM cases WHERE id = ?', [id]))[0] ?? null;

  return {
    /**
     * Список дел по фильтру.
     *
     * Пустой фильтр по статусу и типу разворачивается в полный набор значений —
     * запросу не нужен признак «включено ли условие». С тегами так нельзя: дело без
     * тегов не должно исчезать из списка, когда фильтр по тегам не задан.
     */
    async list(filter: CaseFilter = {}): Promise<Case[]> {
      const statuses = filter.statuses?.length ? filter.statuses : ALL_STATUSES;
      const types = filter.types?.length ? filter.types : ALL_TYPES;
      const tagIds = filter.tagIds ?? [];

      const rows = await db.query(
        `SELECT * FROM cases
          WHERE deletedAt IS NULL
            AND status IN (${statuses.map(() => '?').join(',')})
            AND type IN (${types.map(() => '?').join(',')})
            AND (? IS NULL OR clientId = ?)
            AND (
                  ? = 0
                  OR id IN (SELECT caseId FROM case_tags WHERE tagId IN (${
                    tagIds.length ? tagIds.map(() => '?').join(',') : 'NULL'
                  }))
                )
          ORDER BY updatedAt DESC`,
        [
          ...statuses,
          ...types,
          filter.clientId ?? null,
          filter.clientId ?? null,
          tagIds.length ? 1 : 0,
          ...tagIds,
        ],
      );
      return rows.map(toCase);
    },

    async find(id: string): Promise<Case | null> {
      const rows = await db.query('SELECT * FROM cases WHERE id = ? AND deletedAt IS NULL', [id]);
      return rows[0] ? toCase(rows[0]) : null;
    },

    async recent(limit = 5): Promise<Case[]> {
      const rows = await db.query(
        'SELECT * FROM cases WHERE deletedAt IS NULL ORDER BY updatedAt DESC LIMIT ?',
        [limit],
      );
      return rows.map(toCase);
    },

    async create(draft: Omit<Case, 'createdAt' | 'updatedAt'> & { id?: string }) {
      return guard(async () => {
        const id = draft.id?.trim() ? draft.id : newId();
        const at = now();

        await db.run(
          `INSERT INTO cases (id, title, clientId, type, status, description, notes, links,
                              isPinned, typeOther, sortOrder, createdAt, updatedAt, deletedAt)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, NULL)`,
          [
            id,
            draft.title,
            draft.clientId,
            draft.type,
            draft.status,
            draft.description,
            draft.notes,
            formatLinks(draft.links),
            draft.isPinned ? 1 : 0,
            draft.typeOther,
            draft.sortOrder,
            at,
            at,
          ],
        );
        return id;
      });
    },

    async update(value: Case): Promise<DataResult<void>> {
      const existing = await findRaw(value.id);
      if (!existing) return failure({ kind: 'NotFound', id: value.id });

      return guard(async () => {
        await db.run(
          `UPDATE cases SET title = ?, clientId = ?, type = ?, status = ?, description = ?,
                            notes = ?, links = ?, isPinned = ?, typeOther = ?, updatedAt = ?
            WHERE id = ?`,
          [
            value.title,
            value.clientId,
            value.type,
            value.status,
            value.description,
            value.notes,
            formatLinks(value.links),
            value.isPinned ? 1 : 0,
            value.typeOther,
            now(),
            value.id,
          ],
        );
      });
    },

    /**
     * Точечная правка дела: пишутся **только переданные поля**.
     *
     * Не «прочитать дело целиком, подменить поле и записать обратно»: между чтением
     * и записью успевает пройти чужая правка, и она молча теряется. Поймано
     * 04.09.2026 — описание, тег и ссылка, поставленные подряд, оставляли в деле
     * только последнюю запись. В приложении, где хранятся материалы дел, молчаливая
     * потеря правки недопустима.
     */
    async patch(
      id: string,
      change: Partial<
        Pick<
          Case,
          | 'title'
          | 'clientId'
          | 'type'
          | 'status'
          | 'typeOther'
          | 'description'
          | 'notes'
          | 'links'
          | 'isPinned'
        >
      >,
    ): Promise<DataResult<void>> {
      if (!(await findRaw(id))) return failure({ kind: 'NotFound', id });

      const columns: [string, string | number | null][] = [];
      if ('title' in change) columns.push(['title', change.title ?? '']);
      if ('clientId' in change) columns.push(['clientId', change.clientId ?? null]);
      if ('type' in change) columns.push(['type', change.type ?? 'Other']);
      if ('status' in change) columns.push(['status', change.status ?? 'Active']);
      if ('typeOther' in change) columns.push(['typeOther', change.typeOther ?? null]);
      if ('description' in change) columns.push(['description', change.description ?? null]);
      if ('notes' in change) columns.push(['notes', change.notes ?? null]);
      if ('links' in change) columns.push(['links', formatLinks(change.links ?? [])]);
      if ('isPinned' in change) columns.push(['isPinned', change.isPinned ? 1 : 0]);

      if (columns.length === 0) return success(undefined);

      return guard(async () => {
        await db.run(
          `UPDATE cases SET ${columns.map(([name]) => `${name} = ?`).join(', ')}, updatedAt = ?
            WHERE id = ?`,
          [...columns.map(([, value]) => value), now(), id],
        );
      });
    },

    async setPinned(id: string, isPinned: boolean): Promise<DataResult<void>> {
      if (!(await findRaw(id))) return failure({ kind: 'NotFound', id });

      return guard(async () => {
        await db.run('UPDATE cases SET isPinned = ?, updatedAt = ? WHERE id = ?', [
          isPinned ? 1 : 0,
          now(),
          id,
        ]);
      });
    },

    /**
     * Записать свой порядок дел целиком, ровными шагами.
     *
     * **`updatedAt` не трогается намеренно.** У дел это ключ сортировок «Срочность»
     * и «Недавние»: один жест перестановки выдал бы весь список за только что изменённый.
     */
    async reorder(orderedIds: string[]): Promise<DataResult<void>> {
      return guard(async () => {
        await db.transaction(async (tx) => {
          for (const [index, id] of orderedIds.entries()) {
            await tx.run('UPDATE cases SET sortOrder = ? WHERE id = ?', [
              (index + 1) * SORT_STEP,
              id,
            ]);
          }
        });
      });
    },

    /**
     * Мягкое удаление дела вместе с документами и событиями — одной транзакцией.
     *
     * Внешние ключи с CASCADE тут не помогают: мягкое удаление не удаляет строку,
     * поэтому связанные записи помечаются явно. Файлы остаются на диске: пока
     * действует «Отменить», удалять нечего.
     */
    async remove(id: string): Promise<DataResult<void>> {
      if (!(await findRaw(id))) return failure({ kind: 'NotFound', id });

      return guard(async () => {
        const at = now();
        await db.transaction(async (tx) => {
          await tx.run('UPDATE cases SET deletedAt = ?, updatedAt = ? WHERE id = ?', [at, at, id]);
          await tx.run(
            'UPDATE documents SET deletedAt = ?, updatedAt = ? WHERE caseId = ? AND deletedAt IS NULL',
            [at, at, id],
          );
          await tx.run(
            'UPDATE events SET deletedAt = ?, updatedAt = ? WHERE caseId = ? AND deletedAt IS NULL',
            [at, at, id],
          );
        });
      });
    },

    /**
     * Возврат удалённого дела вместе с тем, что ушло **вместе с ним**.
     *
     * Метка времени удаления здесь не формальность: она отличает документы, удалённые
     * заодно с делом, от удалённых юристом раньше и по отдельности. Иначе «Отменить»
     * воскресило бы и то, что выбрасывали сознательно.
     */
    async restore(id: string): Promise<DataResult<void>> {
      const existing = await findRaw(id);
      if (!existing) return failure({ kind: 'NotFound', id });

      const deletedAt = existing.deletedAt ?? null;
      if (deletedAt === null) return success(undefined);

      return guard(async () => {
        const at = now();
        await db.transaction(async (tx) => {
          await tx.run('UPDATE cases SET deletedAt = NULL, updatedAt = ? WHERE id = ?', [at, id]);
          await tx.run(
            'UPDATE documents SET deletedAt = NULL, updatedAt = ? WHERE caseId = ? AND deletedAt = ?',
            [at, id, deletedAt],
          );
          await tx.run(
            'UPDATE events SET deletedAt = NULL, updatedAt = ? WHERE caseId = ? AND deletedAt = ?',
            [at, id, deletedAt],
          );
        });
      });
    },
  };
}

export type CaseRepository = ReturnType<typeof caseRepository>;
